// First-touch attribution cookie for direct app visitors (KB-8753).
// Backend reads cdbl_src at signup (card BE-3). Website sets the same cookie for
// visitors who start there, so never overwrite an existing value: first touch wins.
// Format matches the website worker: a single URLSearchParams-encoded string,
// e.g. first_seen=2026-10-26T10%3A42%3A26.918Z&landing=%2Fagencies%2F&utm_source=t

export const SOURCE_COOKIE_NAME = 'cdbl_src'
const MAX_AGE_SECONDS = 90 * 24 * 60 * 60
const MAX_VALUE_LENGTH = 500
const MAX_TOTAL_LENGTH = 1000
const URL_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'ref']
const DROP_ORDER = ['utm_content', 'utm_campaign', 'utm_term']
const ENTRY_PATH = /^\/tasks\/new\/?$/

const hasCookie = (name) => {
  return document.cookie.split(';').some(c => c.trim().startsWith(`${name}=`))
}

const truncate = (value) => (value || '').slice(0, MAX_VALUE_LENGTH)

// Landing keeps the path and any query params not already stored as separate keys.
// Strips every utm_* (not just the five stored ones) so landing never carries attribution.
const buildLanding = (loc) => {
  const query = new URLSearchParams(loc.search)
  const stripped = [...query.keys()].filter(key => key.startsWith('utm_') || URL_KEYS.includes(key))
  stripped.forEach(key => query.delete(key))
  const search = query.toString()
  return `${loc.pathname}${search ? `?${search}` : ''}`
}

const referrerOriginAndPath = (referrer) => {
  try {
    const url = new URL(referrer)
    return `${url.origin}${url.pathname}`
  } catch (err) {
    return referrer
  }
}

const setIfPresent = (params, key, value) => {
  if (value) {
    params.set(key, value)
  }
}

// Shrink to MAX_TOTAL_LENGTH: drop low-value utm keys, then trim referrer, then landing.
const fitToLimit = (params) => {
  const over = () => params.toString().length > MAX_TOTAL_LENGTH

  for (const key of DROP_ORDER) {
    if (!over()) return params
    params.delete(key)
  }

  if (over() && params.has('referrer')) {
    params.set('referrer', referrerOriginAndPath(params.get('referrer')))
  }

  while (over() && params.get('landing')) {
    const excess = params.toString().length - MAX_TOTAL_LENGTH
    const landing = params.get('landing')
    // Encoded chars take up to 3x, so cut at least a third of the excess each pass.
    const cut = Math.max(1, Math.ceil(excess / 3))
    params.set('landing', landing.slice(0, Math.max(0, landing.length - cut)))
  }

  return params
}

export const buildSourceValue = (loc = window.location, referrer = document.referrer, now = new Date()) => {
  const params = new URLSearchParams()
  params.set('first_seen', now.toISOString())
  params.set('landing', truncate(buildLanding(loc)))
  setIfPresent(params, 'referrer', truncate(referrer))

  const query = new URLSearchParams(loc.search)
  URL_KEYS.forEach(key => setIfPresent(params, key, truncate(query.get(key))))

  return fitToLimit(params).toString()
}

// Only direct /tasks/new visitors get tagged here; everyone else is tagged by the website.
export const writeSourceCookie = (loc = window.location) => {
  try {
    if (!ENTRY_PATH.test(loc.pathname) || hasCookie(SOURCE_COOKIE_NAME)) {
      return false
    }

    const domain = loc.hostname.endsWith('codeable.io') ? '; Domain=.codeable.io' : ''
    const secure = loc.protocol === 'https:' ? '; Secure' : ''
    const value = buildSourceValue(loc)

    document.cookie = `${SOURCE_COOKIE_NAME}=${value}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${domain}${secure}`
    return true
  } catch (err) {
    // Never let attribution break app boot
    return false
  }
}
